import React from "react";
import { SkeletonBlock, SkeletonText } from "./GenericSkeleton";

export interface TableSkeletonProps {
  /** Number of columns to display in the skeleton */
  columns?: number;
  /** Number of rows to display in the skeleton */
  rows?: number;
  /** Additional className for the container */
  className?: string;
}

/**
 * Reusable table skeleton component for displaying loading state during data fetch.
 * 
 * @example
 * ```tsx
 * {isFetching ? (
 *   <TableSkeleton columns={5} rows={5} />
 * ) : (
 *   <GenericTable columns={columns} data={data} />
 * )}
 * ```
 */
export const TableSkeleton: React.FC<TableSkeletonProps> = ({
  columns = 5,
  rows = 5,
  className = "",
}) => {
  return (
    <div className={`border border-semantic-border-secondary rounded-t-sm shadow-sm bg-semantic-bg-primary ${className}`}>
      {/* Table header skeleton */}
      <div className="border-b border-semantic-border-secondary p-4 bg-semantic-bg-primary">
        <div className="flex gap-4">
          {Array.from({ length: columns }).map((_, colIdx) => (
            <SkeletonBlock key={colIdx} h={20} w="100px" className="flex-1" />
          ))}
        </div>
      </div>
      
      {/* Table rows skeleton */}
      <div className="space-y-2 p-4">
        {Array.from({ length: rows }).map((_, idx) => (
          <div key={idx} className="flex gap-4">
            {Array.from({ length: columns }).map((_, colIdx) => (
              <SkeletonText key={colIdx} lines={1} lineHeight={20} className="flex-1" />
            ))}
          </div>
        ))}
      </div>
      
      {/* Footer skeleton.
          Wraps on narrow viewports (e.g. inside modals on mobile) so the
          ~600px of fixed-width blocks don't overflow the parent card. */}
      <div className="border-t border-semantic-border-secondary bg-semantic-bg-primary p-3 overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-y-2 gap-x-3">
          <SkeletonBlock h={20} w="100px" />
          <div className="flex flex-wrap items-center gap-2">
            <SkeletonBlock h={32} w="32px" />
            <SkeletonBlock h={32} w="32px" />
            <SkeletonBlock h={20} w="80px" />
            <SkeletonBlock h={32} w="32px" />
            <SkeletonBlock h={32} w="32px" />
            <SkeletonBlock h={32} w="120px" />
            <SkeletonBlock h={20} w="100px" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TableSkeleton;
